/* Mobile sticky add-to-cart bar (snippets/product-sticky-atc-mobile.liquid).

   - Shows whenever the main Add to cart button is off screen, both before
     the shopper reaches it and after it has scrolled away; hides while it
     is on screen. Hides again past a share of the page (setting, 85% by
     default) and whenever the footer is in view. Always shown in the theme
     editor's preview mode.
   - Sits right on the theme's mobile navigation bar, whose real height it
     measures (the theme's --height-bottom-bar is only an estimate).
   - Its variant list drives the page's own <variant-selects>: the chosen
     variant's values are set on the main picker and a change event is
     fired on it, so the theme's variant code runs exactly as if the shopper
     had used the main picker. Both directions stay in sync through the
     theme's "variant-change" event, which also hands over the freshly
     rendered section to copy the price and button state from.
   - Add to cart is the theme's own <product-form>. */
if (!customElements.get('mobile-sticky-atc')) {
  class MobileStickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.preview = this.hasAttribute('data-preview');
      this.hideAfter = Number(this.dataset.hideAfter) || 85;
      this.card = this.querySelector('.msticky__card');
      this.toggle = this.querySelector('[data-msticky-toggle]');
      this.list = this.querySelector('[role="listbox"]');
      this.label = this.querySelector('[data-msticky-label]');
      this.price = this.querySelector(`#MobileStickyPrice-${this.sectionId}`);
      this.addButton = this.querySelector(`#MobileStickySubmit-${this.sectionId}`);
      this.addText = this.querySelector('[data-msticky-add-text]');
      this.addLabel = this.addText ? this.addText.textContent.trim() : '';
      this.options = Array.from(this.querySelectorAll('.msticky__option'));
      this.mobile = window.matchMedia('(max-width: 749px)');
      try {
        this.optionNames = JSON.parse(this.querySelector('[data-msticky-product]').textContent).options || [];
      } catch (error) {
        this.optionNames = [];
      }

      /* ---- position: on top of the navigation bar ---- */
      this.nav = document.querySelector('.botton-nav');
      this.placeOnNav = () => {
        /* 0 while the bar is hidden (display: none above phone width). */
        const height = this.nav ? this.nav.getBoundingClientRect().height : 0;
        this.navHeight = Math.round(height);
        this.style.setProperty('--msticky-bottom', `${this.navHeight}px`);
        this.classList.toggle('msticky--over-nav', height > 0);
      };
      this.placeOnNav();
      if (this.nav && 'ResizeObserver' in window) {
        this.navObserver = new ResizeObserver(this.placeOnNav);
        this.navObserver.observe(this.nav);
      }

      /* ---- visibility ---- */
      this.frame = 0;
      this.onScroll = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.placeOnNav();
          this.updateVisibility();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.updateVisibility();

      /* ---- variant list ---- */
      if (this.toggle && this.list) {
        this.onToggle = () => (this.list.hidden ? this.open() : this.close());
        this.onChoose = (event) => {
          const option = event.target.closest('.msticky__option');
          if (option) this.choose(option);
        };
        this.onKeyDown = (event) => this.handleKeys(event);
        this.onOutside = (event) => {
          if (!this.list.hidden && !this.contains(event.target)) this.close();
        };
        this.toggle.addEventListener('click', this.onToggle);
        this.list.addEventListener('click', this.onChoose);
        this.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('pointerdown', this.onOutside);
      }

      /* ---- a variant switch, from either picker ----
         The main button goes disabled the moment the variant changes and
         comes back once the theme has the new variant's state; this one
         does the same. The theme's picker handles the change on itself
         first, so by the time it reaches the document its currentVariant
         is already worked out. */
      this.onPickerChange = (event) => {
        const picker = event.target.closest && event.target.closest('variant-selects');
        if (!picker || picker.dataset.section !== this.sectionId) return;
        this.setPending(picker.currentVariant);
      };
      document.addEventListener('change', this.onPickerChange);

      /* ---- the theme's variant event ---- */
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onThemeVariantChange(event));
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.navObserver) this.navObserver.disconnect();
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
      if (this.list) this.list.removeEventListener('click', this.onChoose);
      this.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('pointerdown', this.onOutside);
      document.removeEventListener('change', this.onPickerChange);
      if (this.unsubscribe) this.unsubscribe();
      document.documentElement.style.removeProperty('--height-sticky-add-to-cart');
    }

    updateVisibility() {
      const target = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      const footer = document.querySelector('footer');
      /* On screen means between the top of the screen and the top of the
         navigation bar, which covers whatever sits behind it. */
      const rect = target ? target.getBoundingClientRect() : null;
      const buttonInView = rect ? rect.bottom > 0 && rect.top < window.innerHeight - (this.navHeight || 0) : false;
      const footerInView = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
      const pastLimit = this.hideAfter < 100 && progress >= this.hideAfter;
      const show = this.preview || (!buttonInView && !footerInView && !pastLimit);

      this.classList.toggle('is-visible', show);
      if (show) this.removeAttribute('inert');
      else {
        this.setAttribute('inert', '');
        this.close();
      }

      /* The theme's phone scroll-to-top button uses this as its distance
         from the bottom of the screen: navigation bar, this bar, and a little
         breathing room. From layout sizes, so it is right even while the bar
         is still sliding in. */
      if (show && this.mobile.matches && this.card) {
        const offset = (this.navHeight || 0) + this.card.offsetHeight + 8;
        document.documentElement.style.setProperty('--height-sticky-add-to-cart', `${offset}px`);
      } else {
        document.documentElement.style.removeProperty('--height-sticky-add-to-cart');
      }
    }

    /* ---------------- the list ---------------- */
    open() {
      this.list.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      const selected = this.options.find((option) => option.classList.contains('is-selected')) || this.options[0];
      if (selected) {
        selected.focus({ preventScroll: true });
        selected.scrollIntoView({ block: 'nearest' });
      }
    }

    close(returnFocus = false) {
      if (!this.list || this.list.hidden) return;
      this.list.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.toggle.focus();
    }

    handleKeys(event) {
      if (!this.list || this.list.hidden) return;
      const index = this.options.indexOf(document.activeElement);
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = this.options[Math.min(this.options.length - 1, Math.max(0, index + step))];
        if (next) next.focus();
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.options[event.key === 'Home' ? 0 : this.options.length - 1].focus();
      } else if (event.key === 'Tab') {
        this.close();
      }
    }

    choose(option) {
      const variantId = option.dataset.variantId;
      let values = [];
      try {
        values = JSON.parse(option.dataset.variantOptions);
      } catch (error) {
        values = [];
      }
      this.markSelected(variantId, option.dataset.variantTitle);
      this.close(true);

      const picker =
        document.getElementById(`variant-selects-${this.sectionId}`) ||
        document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);

      if (picker && values.length) {
        /* Set every option first, then fire one change: the theme's picker
           reads all of its controls when it handles the event. */
        let changed = null;
        this.optionNames.forEach((name, i) => {
          const value = values[i];
          const select = Array.from(picker.querySelectorAll('select')).find((el) => el.name === `options[${name}]`);
          if (select) {
            if (select.value !== value) {
              select.value = value;
              changed = select;
            }
            return;
          }
          const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find(
            (el) => el.name === name && el.value === value
          );
          if (radio && !radio.checked) {
            radio.checked = true;
            changed = radio;
          }
        });
        if (changed) changed.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      /* No picker on the page: point the forms at the variant and refresh
         this bar from the section, the way the theme's picker would. */
      document
        .querySelectorAll(`#product-form-${this.sectionId} input[name="id"], #MobileStickyAtcForm-${this.sectionId} input[name="id"]`)
        .forEach((input) => {
          input.value = variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      this.setPending({ id: variantId });
      fetch(`${window.location.pathname}?variant=${variantId}&section_id=${this.sectionId}`)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          this.refreshFrom(html, { id: Number(variantId), title: option.dataset.variantTitle, available: option.dataset.available === 'true' });
        })
        .catch(() => {});
    }

    /* Disabled while the switch is in flight, like the main button. A
       combination that matches no variant stays disabled and says so. */
    setPending(variant) {
      if (!this.addButton) return;
      this.addButton.disabled = true;
      if (!variant && this.addText) {
        this.addText.textContent = window.variantStrings?.unavailable || this.addText.textContent;
      }
    }

    /* ---------------- staying in sync ---------------- */
    onThemeVariantChange(event) {
      const data = event && event.data;
      if (!data || !data.variant || String(data.sectionId) !== String(this.sectionId)) return;
      this.refreshFrom(data.html, data.variant);
    }

    refreshFrom(html, variant) {
      this.markSelected(String(variant.id), variant.title);

      const freshPrice = html && html.getElementById(`MobileStickyPrice-${this.sectionId}`);
      if (freshPrice && this.price) this.price.innerHTML = freshPrice.innerHTML;

      if (this.addButton && this.addText) {
        const freshAdd = html && html.getElementById(`MobileStickySubmit-${this.sectionId}`);
        const disabled = freshAdd ? freshAdd.hasAttribute('disabled') : !variant.available;
        this.addButton.disabled = disabled;
        const freshText = freshAdd && freshAdd.querySelector('[data-msticky-add-text]');
        this.addText.textContent = freshText
          ? freshText.textContent.trim()
          : disabled
            ? window.variantStrings?.soldOut || this.addLabel
            : this.addLabel;
      }

      const input = this.querySelector('input[name="id"]');
      if (input) input.value = variant.id;
    }

    markSelected(variantId, title) {
      this.options.forEach((option) => {
        const on = option.dataset.variantId === String(variantId);
        option.classList.toggle('is-selected', on);
        option.parentElement.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (title && this.label) this.setLabel(title);
    }

    /* Same split as the Liquid label: bold before the first "(", medium after. */
    setLabel(title) {
      const cut = title.indexOf('(');
      const main = document.createElement('strong');
      main.className = 'msticky__label-main';
      main.textContent = (cut === -1 ? title : title.slice(0, cut)).trim();
      this.label.replaceChildren(main);
      if (cut !== -1) {
        const rest = document.createElement('span');
        rest.className = 'msticky__label-rest';
        rest.textContent = title.slice(cut);
        this.label.append(' ', rest);
      }
      const prefix = (this.toggle.getAttribute('aria-label') || '').split(':')[0];
      this.toggle.setAttribute('aria-label', `${prefix}: ${title}`);
    }
  }

  customElements.define('mobile-sticky-atc', MobileStickyAtc);
}
